import { html, nothing, type TemplateResult } from "lit";
import "../components/fleet-toggle.ts";
import {
  availableRoleModels,
  canSubmitNewRole,
  FLEET_ROLE_PANELS,
  roleModelFallbacks,
  roleModelLabel,
  roleModelPrimary,
  type FleetRoleDraft,
  type FleetRoleEditorState,
  type FleetRolePanel,
} from "../controllers/fleet-roles.ts";
import { icons } from "../icons.ts";

export type FleetRoleEditorProps = {
  state: FleetRoleEditorState;
  configForm: Record<string, unknown> | null;
  connected: boolean;
  onSearch: (value: string) => void;
  onSelectRole: (roleId: string) => void;
  onSelectPanel: (panel: FleetRolePanel) => void;
  onSelectFile: (name: "AGENTS.md" | "SOUL.md" | "TOOLS.md") => void;
  onToggleAdd: (open: boolean) => void;
  onNewRoleField: (
    field: "newRoleName" | "newRoleDescription" | "newRoleSkills",
    value: string,
  ) => void;
  onCreateRole: () => void;
  onPatchRole: (patch: Partial<FleetRoleDraft>) => void;
  onAddSkill: () => void;
  onRemoveSkill: (skill: string) => void;
  onSkillDraft: (field: "newSkillName" | "newSkillDescription", value: string) => void;
  onSave: () => void;
};

const ROLE_ICONS: Record<string, string> = {
  "jasiu-coordinator": "</>",
  planner: "◈",
  designer: "◐",
  implementator: "{ }",
  tester: "△",
  reviewer: "○",
};

export function renderFleetRoleEditor(props: FleetRoleEditorProps) {
  const filtered = props.state.roles.filter((role) => {
    const q = props.state.search.trim().toLowerCase();
    if (!q) {
      return true;
    }
    return (
      role.name.toLowerCase().includes(q) ||
      role.roleId.toLowerCase().includes(q) ||
      role.description.toLowerCase().includes(q)
    );
  });
  const selected =
    props.state.roles.find((role) => role.roleId === props.state.selectedId) ?? filtered[0] ?? null;
  const configured = props.state.roles.filter(
    (role) => !role.builtIn || Boolean(role.model),
  ).length;

  return html`
    <div class="bible-roles" data-testid="fleet-role-editor">
      <aside class="bible-roles__sidebar" aria-label="Agent roles">
        <div class="bible-roles__sidebar-head">
          <div>
            <div class="card-title">Agent roles</div>
            <div class="card-sub">${configured} configured</div>
          </div>
          <button
            type="button"
            class="btn btn--sm primary"
            title="Add a role. Capabilities and skills are required."
            aria-label="Add role"
            data-testid="fleet-role-add"
            @click=${() => props.onToggleAdd(!props.state.addOpen)}
          >
            + Add role
          </button>
        </div>
        <label class="bible-roles__search">
          <span class="sr-only">Search roles</span>
          <input
            type="search"
            placeholder="Search roles…"
            data-testid="fleet-role-search"
            .value=${props.state.search}
            @input=${(event: Event) => props.onSearch((event.target as HTMLInputElement).value)}
          />
        </label>
        ${
          props.state.addOpen
            ? html`
                <form
                  class="bible-roles__add"
                  data-testid="fleet-role-add-form"
                  @submit=${(event: Event) => {
                    event.preventDefault();
                    props.onCreateRole();
                  }}
                >
                  <label>
                    <span>Role name</span>
                    <input
                      required
                      .value=${props.state.newRoleName}
                      @input=${(event: Event) =>
                        props.onNewRoleField(
                          "newRoleName",
                          (event.target as HTMLInputElement).value,
                        )}
                    />
                  </label>
                  <label>
                    <span>Capabilities</span>
                    <textarea
                      required
                      rows="3"
                      placeholder="What this role can do — Jasiu reads this when picking an executor."
                      .value=${props.state.newRoleDescription}
                      @input=${(event: Event) =>
                        props.onNewRoleField(
                          "newRoleDescription",
                          (event.target as HTMLTextAreaElement).value,
                        )}
                    ></textarea>
                  </label>
                  <label>
                    <span>Skills (comma-separated)</span>
                    <input
                      required
                      placeholder="planning, review"
                      .value=${props.state.newRoleSkills}
                      @input=${(event: Event) =>
                        props.onNewRoleField(
                          "newRoleSkills",
                          (event.target as HTMLInputElement).value,
                        )}
                    />
                  </label>
                  <button
                    type="submit"
                    class="btn btn--sm primary"
                    data-testid="fleet-role-create"
                    ?disabled=${!canSubmitNewRole(props.state)}
                  >
                    Create role
                  </button>
                </form>
              `
            : nothing
        }
        <div class="bible-roles__list" role="listbox" aria-label="Fleet roles">
          ${filtered.map((role) => {
            const active = selected?.roleId === role.roleId;
            return html`
              <button
                type="button"
                role="option"
                aria-selected=${active}
                class="bible-roles__row ${active ? "is-active" : ""}"
                title=${`${role.name}: ${role.description}`}
                data-testid="fleet-role-row"
                data-role-id=${role.roleId}
                @click=${() => props.onSelectRole(role.roleId)}
              >
                <span class="bible-roles__avatar" aria-hidden="true">
                  ${ROLE_ICONS[role.roleId] ?? role.name.slice(0, 1)}
                </span>
                <span class="bible-roles__row-copy">
                  <span class="bible-roles__row-name">${role.name}</span>
                  <span class="bible-roles__row-purpose">${role.description}</span>
                </span>
              </button>
            `;
          })}
        </div>
        <p class="bible-roles__footnote">
          All instances of a role in an engagement share one role-config version.
        </p>
      </aside>
      <section class="bible-roles__main">
        ${selected ? renderRoleDetail(selected, props) : renderEmptyRole()}
      </section>
    </div>
  `;
}

function renderEmptyRole() {
  return html`
    <div class="card">
      <div class="card-title">Select a role</div>
      <div class="card-sub">Pick a role to edit its skills, model, and instruction files.</div>
    </div>
  `;
}

function renderRoleDetail(role: FleetRoleDraft, props: FleetRoleEditorProps) {
  return html`
    <header class="bible-roles__header">
      <span class="bible-roles__avatar bible-roles__avatar--lg" aria-hidden="true">
        ${ROLE_ICONS[role.roleId] ?? role.name.slice(0, 1)}
      </span>
      <div>
        <div class="card-title">${role.name}</div>
        <div class="card-sub">${role.description}</div>
      </div>
      <span class="bible-roles__version" title="Role configuration version">v${role.version}</span>
    </header>
    <div class="bible-roles__tabs" role="tablist" aria-label="Role sections">
      ${FLEET_ROLE_PANELS.map(
        (tab) => html`
          <button
            type="button"
            role="tab"
            class="bible-roles__tab ${props.state.panel === tab.id ? "is-active" : ""}"
            aria-selected=${props.state.panel === tab.id}
            data-testid=${`fleet-role-tab-${tab.id}`}
            @click=${() => props.onSelectPanel(tab.id)}
          >
            ${tab.label}
          </button>
        `,
      )}
    </div>
    ${props.state.panel === "overview" ? renderOverview(role, props) : nothing}
    ${props.state.panel === "skills" ? renderSkills(role, props) : nothing}
    ${props.state.panel === "files" ? renderFiles(role, props) : nothing}
    ${props.state.panel === "advanced" ? renderAdvanced(role, props) : nothing}
    <footer class="bible-roles__footer">
      <span class="muted">
        ${
          props.state.dirty
            ? "Unsaved changes apply to new work. Running tasks keep their versioned configuration."
            : "Preview settings saved"
        }
      </span>
      <button
        type="button"
        class="btn primary"
        ?disabled=${!props.state.dirty || props.state.saving || !props.connected}
        title="Save role configuration"
        @click=${props.onSave}
      >
        ${props.state.saving ? "Saving…" : "Save changes"}
      </button>
    </footer>
    ${
      props.state.error
        ? html`<div class="callout danger" style="margin-top: 12px;">${props.state.error}</div>`
        : nothing
    }
  `;
}

function renderOverview(role: FleetRoleDraft, props: FleetRoleEditorProps) {
  const models = availableRoleModels(props.configForm);
  const primary = roleModelPrimary(role);
  return html`
    <section class="card bible-roles__card">
      <div class="card-title">Essentials</div>
      <div class="card-sub">
        Identity and model configuration. Instructions are managed in AGENTS.md.
      </div>
      <label class="bible-roles__field">
        <span>Role name</span>
        <input
          .value=${role.name}
          @input=${(event: Event) =>
            props.onPatchRole({ name: (event.target as HTMLInputElement).value })}
        />
      </label>
      <label class="bible-roles__field">
        <span>Purpose</span>
        <textarea
          rows="3"
          .value=${role.description}
          @input=${(event: Event) =>
            props.onPatchRole({ description: (event.target as HTMLTextAreaElement).value })}
        ></textarea>
        <span class="bible-roles__purpose-meta">
          <span class="muted">Read from AGENTS.md → Purpose</span>
          <button
            type="button"
            class="btn btn--sm"
            data-testid="fleet-role-edit-in-files"
            @click=${() => {
              props.onSelectPanel("files");
              props.onSelectFile("AGENTS.md");
            }}
          >
            Edit in Files
          </button>
        </span>
      </label>
      <label class="bible-roles__field">
        <span>Primary model</span>
        <select
          data-testid="fleet-role-model"
          .value=${primary}
          title="Assign a model to this role. Agents and subagents inherit it."
          @change=${(event: Event) => {
            const value = (event.target as HTMLSelectElement).value;
            const fallbacks = roleModelFallbacks(role);
            props.onPatchRole({
              model: value
                ? { primary: value, ...(fallbacks.length ? { fallbacks } : {}) }
                : undefined,
            });
          }}
        >
          ${models.map((option) => html`<option value=${option.value}>${option.label}</option>`)}
        </select>
      </label>
      <p class="bible-roles__hint">
        Assigned to this role, not individual agents or subagents. Current:
        ${roleModelLabel(role, props.configForm)}.
      </p>
    </section>
  `;
}

function renderSkills(role: FleetRoleDraft, props: FleetRoleEditorProps) {
  return html`
    <section class="card bible-roles__card">
      <div class="bible-roles__card-head">
        <div>
          <div class="card-title">Assigned skills (${role.skills.length})</div>
          <div class="card-sub">Skills describe what this role is equipped to do.</div>
        </div>
      </div>
      <div class="bible-roles__skills">
        ${
          role.skills.length === 0
            ? html`<p class="muted">
                No skills yet. Add at least one so Jasiu can pick this role.
              </p>`
            : role.skills.map(
                (skill) => html`
                  <div class="bible-roles__skill">
                    <div>
                      <div class="bible-roles__skill-name">${skill}</div>
                    </div>
                    <button
                      type="button"
                      class="bible-roles__icon-btn"
                      title=${`Remove ${skill}`}
                      aria-label=${`Remove ${skill}`}
                      @click=${() => props.onRemoveSkill(skill)}
                    >
                      ×
                    </button>
                  </div>
                `,
              )
        }
      </div>
      <div class="bible-roles__skill-add">
        <input
          placeholder="Skill name"
          .value=${props.state.newSkillName}
          @input=${(event: Event) =>
            props.onSkillDraft("newSkillName", (event.target as HTMLInputElement).value)}
        />
        <input
          placeholder="Short description"
          .value=${props.state.newSkillDescription}
          @input=${(event: Event) =>
            props.onSkillDraft("newSkillDescription", (event.target as HTMLInputElement).value)}
        />
        <button
          type="button"
          class="btn primary"
          ?disabled=${!props.state.newSkillName.trim()}
          @click=${props.onAddSkill}
        >
          Add
        </button>
      </div>
    </section>
  `;
}

function renderFiles(role: FleetRoleDraft, props: FleetRoleEditorProps) {
  const names = ["AGENTS.md", "SOUL.md", "TOOLS.md"] as const;
  const selectedFile = props.state.activeFile;
  return html`
    <section class="card bible-roles__card bible-roles__files">
      <div class="card-title">Role instruction files</div>
      <div class="card-sub">
        AGENTS.md contains the description shown on Overview. Other files provide additional
        guidance.
      </div>
      <div class="bible-roles__files-grid">
        <div class="bible-roles__file-list" role="listbox" aria-label="Role files">
          ${names.map((name) => {
            const selected = selectedFile === name;
            return html`
              <button
                type="button"
                role="option"
                class="bible-roles__file ${selected ? "is-active" : ""}"
                aria-selected=${selected}
                data-file=${name}
                @click=${() => props.onSelectFile(name)}
              >
                ${name}
              </button>
            `;
          })}
        </div>
        <label class="bible-roles__file-editor">
          <span>${selectedFile}</span>
          <textarea
            rows="16"
            .value=${role.files[selectedFile]}
            @input=${(event: Event) => {
              const next = (event.target as HTMLTextAreaElement).value;
              const files = { ...role.files, [selectedFile]: next };
              const patch: Partial<FleetRoleDraft> = { files };
              if (selectedFile === "AGENTS.md") {
                const purpose = purposeFromAgentsMd(next);
                if (purpose) {
                  patch.description = purpose;
                }
              }
              props.onPatchRole(patch);
            }}
          ></textarea>
        </label>
      </div>
    </section>
  `;
}

function purposeFromAgentsMd(markdown: string): string | null {
  const match = markdown.match(/## Purpose\n([^\n#]+)/);
  const purpose = match?.[1]?.trim();
  return purpose || null;
}

function renderAdvanced(role: FleetRoleDraft, props: FleetRoleEditorProps) {
  const fallbacks = roleModelFallbacks(role).join(", ");
  const models = availableRoleModels(props.configForm);
  return html`
    <section class="card bible-roles__card">
      <div class="card-title">Advanced configuration</div>
      <div class="card-sub">Only the technical options that need role-level customization.</div>
      <div class="card-title" style="margin-top: 16px;">Allowed tools</div>
      <div class="bible-roles__tools">
        ${renderToolToggle(
          role,
          props,
          "files",
          "File access",
          "Read and update project files.",
          icons.folder,
        )}
        ${renderToolToggle(
          role,
          props,
          "terminal",
          "Terminal",
          "Run allowed commands.",
          icons.codeXml,
        )}
        ${renderToolToggle(
          role,
          props,
          "git",
          "Git",
          "Work with files and commits.",
          icons.workflow,
        )}
        ${renderToolToggle(
          role,
          props,
          "browser",
          "Browser",
          "Browse permitted websites.",
          icons.globe,
        )}
      </div>
      <label class="bible-roles__field">
        <span>Fallback model</span>
        <select
          .value=${roleModelFallbacks(role)[0] ?? ""}
          title="Fallback is applied according to the role's approved recovery policy."
          @change=${(event: Event) => {
            const value = (event.target as HTMLSelectElement).value;
            const primary = roleModelPrimary(role);
            props.onPatchRole({
              model:
                primary || value
                  ? { ...(primary ? { primary } : {}), fallbacks: value ? [value] : [] }
                  : undefined,
            });
          }}
        >
          <option value="">Inherit platform policy</option>
          ${models
            .filter((option) => option.value)
            .map((option) => html`<option value=${option.value}>${option.label}</option>`)}
        </select>
      </label>
      <p class="bible-roles__hint">
        Workspace and execution limits are inherited from project settings.
        ${fallbacks ? ` Fallbacks: ${fallbacks}.` : ""}
      </p>
    </section>
  `;
}

function renderToolToggle(
  role: FleetRoleDraft,
  props: FleetRoleEditorProps,
  key: keyof FleetRoleDraft["allowedTools"],
  label: string,
  hint: string,
  icon: TemplateResult,
) {
  const checked = role.allowedTools[key];
  return html`
    <div class="bible-roles__tool-card">
      <span class="bible-roles__tool-icon" aria-hidden="true">${icon}</span>
      <span class="bible-roles__tool-copy">
        <strong>${label}</strong>
        <span class="muted">${hint}</span>
      </span>
      <fleet-toggle
        data-testid=${`fleet-role-tool-${key}`}
        ?checked=${checked}
        @change=${(event: CustomEvent<{ checked: boolean }>) =>
          props.onPatchRole({
            allowedTools: {
              ...role.allowedTools,
              [key]: event.detail.checked,
            },
          })}
      ></fleet-toggle>
    </div>
  `;
}
